import { RefObject, useEffect, useRef } from 'react';

interface NativeInputHandlers {
  onKeyDown?: (event: KeyboardEvent) => void;
  onBlur?: (input: HTMLInputElement) => void;
}

/**
 * Listens on the picker's input natively, so the handlers run before React's delegated ones and
 * before rc-picker resets the typed text on blur. Handlers may change on every render.
 */
export const useNativeInputEvents = (
  pickerRef: RefObject<{ nativeElement: HTMLElement | null } | null>,
  handlers: NativeInputHandlers,
) => {
  const latestHandlers = useRef(handlers);
  latestHandlers.current = handlers;

  useEffect(() => {
    const input = pickerRef.current?.nativeElement?.querySelector('input');
    if (!input) return undefined;

    const onKeyDown = (event: KeyboardEvent) => latestHandlers.current.onKeyDown?.(event);
    const onBlur = () => latestHandlers.current.onBlur?.(input);
    input.addEventListener('keydown', onKeyDown);
    input.addEventListener('blur', onBlur);
    return () => {
      input.removeEventListener('keydown', onKeyDown);
      input.removeEventListener('blur', onBlur);
    };
  }, [pickerRef]);
};
